import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  useChargeApiV1PaymentsPaymentIdChargePost,
  useListPaymentMethodsApiV1PaymentMethodsGet,
  useMarkPaidApiV1PaymentsPaymentIdMarkPaidPost,
  useUploadFilesApiV1FilesPost,
} from '#/shared/api/generated/billing/theCargoBilling'
import type { PaymentMethodBrief } from '#/shared/api/generated/billing/models'
import { formatUsd } from '#/shared/lib/format'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'
import { methodToChargeType } from '#/shared/ui/create-payment-modal'
import type { PaymentMethodType } from '#/shared/ui/create-payment-modal'

import { isReceivableViaOther } from './methods'
import type { SavedCard } from './saved-cards'
import { useSettleCards } from './use-settle-cards'

export const SETTLE_VIA = {
  card: 'card',
  other: 'other',
} as const

export type SettleVia = (typeof SETTLE_VIA)[keyof typeof SETTLE_VIA]

export const SETTLE_VIEW = {
  form: 'form',
  recorded: 'recorded',
} as const

export type SettleView = (typeof SETTLE_VIEW)[keyof typeof SETTLE_VIEW]

const PAYMENT_LIST_QUERY_PREFIX = '/api/v1/payments'
const TOOLBAR_PAYMENT_QUERY_PREFIX = '/api/v1/toolbar/payment'
const PAYMENT_METHOD_ACTIVE_STATUS = 'active'
const PAYMENT_METHOD_PAGE_SIZE = 100

const TOAST_KEYS = {
  charge: {
    success: 'settlePayment.toast.chargeSuccess',
    error: 'settlePayment.toast.chargeError',
    noCardGateway: 'settlePayment.toast.noCardGateway',
  },
  record: {
    success: 'settlePayment.toast.recordSuccess',
    error: 'settlePayment.toast.recordError',
  },
  upload: {
    error: 'settlePayment.toast.uploadError',
  },
} as const

function parseMoney(raw: string): number {
  const n = Number(raw.replace(/[^\d.]/g, ''))
  return Number.isFinite(n) ? n : 0
}

export interface UseSettlePaymentArgs {
  open: boolean
  paymentId: string
  shipmentId: string | undefined
  customerId: string | undefined
  defaultAmount: string
  methods: ReadonlyArray<PaymentMethodBrief>
}

export interface UseSettlePaymentResult {
  view: SettleView
  via: SettleVia
  setVia: (next: SettleVia) => void
  selectedCardId: string
  setSelectedCardId: (next: string) => void
  selectedMethod: string | undefined
  onSelectMethod: (method: string) => void
  recordedMethods: ReadonlyArray<string>
  amount: string
  setAmount: (next: string) => void
  reference: string
  setReference: (next: string) => void
  date: Date
  setDate: (next: Date) => void
  proofFiles: ReadonlyArray<File>
  addProofFiles: (files: ReadonlyArray<File>) => void
  removeProofFile: (index: number) => void
  savedCards: ReadonlyArray<SavedCard>
  isCardsLoading: boolean
  availableMethods: ReadonlyArray<string>
  isMethodsLoading: boolean
  isAmountOverLimit: boolean
  isPartialCharge: boolean
  chargeAmountLabel: string
  isSubmitting: boolean
  submit: () => Promise<void>
}

interface FormState {
  view: SettleView
  setView: (next: SettleView) => void
  via: SettleVia
  setVia: (next: SettleVia) => void
  selectedCardId: string
  setSelectedCardId: (next: string) => void
  selectedMethod: string | undefined
  setSelectedMethod: (next: string | undefined) => void
  amount: string
  setAmount: (next: string) => void
  reference: string
  setReference: (next: string) => void
  date: Date
  setDate: (next: Date) => void
  proofFiles: File[]
  setProofFiles: (next: File[]) => void
}

function useFormState(open: boolean, defaultAmount: string): FormState {
  const [view, setView] = useState<SettleView>(SETTLE_VIEW.form)
  const [via, setVia] = useState<SettleVia>(SETTLE_VIA.card)
  const [selectedCardId, setSelectedCardId] = useState<string>('')
  const [selectedMethod, setSelectedMethod] = useState<string | undefined>(
    undefined,
  )
  const [amount, setAmount] = useState<string>(defaultAmount)
  const [reference, setReference] = useState<string>('')
  const [date, setDate] = useState<Date>(new Date())
  const [proofFiles, setProofFiles] = useState<File[]>([])

  useEffect(() => {
    if (open) return
    setView(SETTLE_VIEW.form)
    setVia(SETTLE_VIA.card)
    setSelectedCardId('')
    setSelectedMethod(undefined)
    setAmount(defaultAmount)
    setReference('')
    setDate(new Date())
    setProofFiles([])
  }, [open, defaultAmount])

  return {
    view,
    setView,
    via,
    setVia,
    selectedCardId,
    setSelectedCardId,
    selectedMethod,
    setSelectedMethod,
    amount,
    setAmount,
    reference,
    setReference,
    date,
    setDate,
    proofFiles,
    setProofFiles,
  }
}

interface SubmitDeps {
  args: UseSettlePaymentArgs
  f: FormState
  chargeMutation: ReturnType<typeof useChargeApiV1PaymentsPaymentIdChargePost>
  markPaidMutation: ReturnType<
    typeof useMarkPaidApiV1PaymentsPaymentIdMarkPaidPost
  >
  uploadMutation: ReturnType<typeof useUploadFilesApiV1FilesPost>
  setUploading: (next: boolean) => void
  invalidate: () => void
  t: ReturnType<typeof useTranslation>['t']
}

async function submitCard(deps: SubmitDeps): Promise<void> {
  const { args, f, chargeMutation, invalidate, t } = deps
  if (!f.selectedCardId) return
  const amount = parseMoney(f.amount)
  try {
    await chargeMutation.mutateAsync({
      paymentId: args.paymentId,
      data: {
        credit_card_id: f.selectedCardId,
        description: f.reference || undefined,
        amount: amount > 0 ? amount : undefined,
      },
    })
    invalidate()
    toast.success(t(TOAST_KEYS.charge.success))
    f.setView(SETTLE_VIEW.recorded)
  } catch (err) {
    toast.error(getApiErrorDetail(err) ?? t(TOAST_KEYS.charge.error))
  }
}

async function uploadProofIfNeeded(
  deps: SubmitDeps,
): Promise<{ ok: true; fileIds: string[] } | { ok: false }> {
  const { f, uploadMutation, setUploading, t } = deps
  if (f.proofFiles.length === 0) return { ok: true, fileIds: [] }
  setUploading(true)
  try {
    const res = await uploadMutation.mutateAsync({
      data: { files: f.proofFiles as unknown as string[] },
    })
    return { ok: true, fileIds: res.data.map((file) => file.id) }
  } catch (err) {
    toast.error(getApiErrorDetail(err) ?? t(TOAST_KEYS.upload.error))
    return { ok: false }
  } finally {
    setUploading(false)
  }
}

async function submitOther(deps: SubmitDeps): Promise<void> {
  const { args, f, markPaidMutation, invalidate, t } = deps
  const method = f.selectedMethod
  const methodType = methodToChargeType(method as PaymentMethodType | undefined)
  if (!method || !methodType) {
    toast.error(t(TOAST_KEYS.charge.noCardGateway))
    return
  }
  const upload = await uploadProofIfNeeded(deps)
  if (!upload.ok) return
  try {
    await markPaidMutation.mutateAsync({
      paymentId: args.paymentId,
      data: {
        method_type: methodType,
        amount: parseMoney(f.amount),
        paid_date: f.date.toISOString().slice(0, 10),
        reference: f.reference || undefined,
        file_ids: upload.fileIds.length > 0 ? upload.fileIds : undefined,
      },
    })
    invalidate()
    toast.success(t(TOAST_KEYS.record.success))
    f.setView(SETTLE_VIEW.recorded)
  } catch (err) {
    toast.error(getApiErrorDetail(err) ?? t(TOAST_KEYS.record.error))
  }
}

function buildSubmit(
  deps: SubmitDeps,
  isAmountOverLimit: boolean,
): () => Promise<void> {
  return () => {
    if (isAmountOverLimit) return Promise.resolve()
    if (deps.f.via === SETTLE_VIA.other) return submitOther(deps)
    return submitCard(deps)
  }
}

function invalidatePayment(
  queryClient: ReturnType<typeof useQueryClient>,
  paymentId: string,
) {
  queryClient.invalidateQueries({ queryKey: [PAYMENT_LIST_QUERY_PREFIX] })
  queryClient.invalidateQueries({ queryKey: [TOOLBAR_PAYMENT_QUERY_PREFIX] })
  queryClient.invalidateQueries({
    queryKey: [`/api/v1/payments/${paymentId}`],
  })
}

interface SettleMethods {
  availableMethods: string[]
  recordedMethods: string[]
  isMethodsLoading: boolean
}

function useSettleMethods(
  open: boolean,
  paymentMethods: ReadonlyArray<PaymentMethodBrief>,
): SettleMethods {
  const { data, isLoading } = useListPaymentMethodsApiV1PaymentMethodsGet(
    { size: PAYMENT_METHOD_PAGE_SIZE, status: PAYMENT_METHOD_ACTIVE_STATUS },
    { query: { enabled: open } },
  )
  const availableMethods = useMemo(() => {
    const types: string[] = []
    for (const method of data?.data.items ?? []) {
      if (!isReceivableViaOther(method.type)) continue
      if (!types.includes(method.type)) types.push(method.type)
    }
    return types
  }, [data])

  const recordedMethods = useMemo(
    () =>
      [...new Set(paymentMethods.map((m) => m.type))].filter((type) =>
        availableMethods.includes(type),
      ),
    [paymentMethods, availableMethods],
  )

  return {
    availableMethods,
    recordedMethods,
    isMethodsLoading: isLoading && open,
  }
}

function useDefaultCard(
  savedCards: ReadonlyArray<SavedCard>,
  selectedCardId: string,
  setSelectedCardId: (next: string) => void,
) {
  useEffect(() => {
    if (selectedCardId || savedCards.length === 0) return
    const defaultCard = savedCards.find((c) => c.isDefault) ?? savedCards[0]
    setSelectedCardId(defaultCard.id)
  }, [savedCards, selectedCardId, setSelectedCardId])
}

function useDefaultVia(
  open: boolean,
  isCardsLoading: boolean,
  hasCards: boolean,
  setVia: (next: SettleVia) => void,
) {
  const applied = useRef(false)
  useEffect(() => {
    if (!open) {
      applied.current = false
      return
    }
    if (isCardsLoading || applied.current) return
    applied.current = true
    setVia(hasCards ? SETTLE_VIA.card : SETTLE_VIA.other)
  }, [open, isCardsLoading, hasCards, setVia])
}

function useRefetchCardsOnCardTab(
  open: boolean,
  via: SettleVia,
  refetchCards: () => void,
) {
  const prevVia = useRef<SettleVia | undefined>(undefined)
  useEffect(() => {
    if (!open) {
      prevVia.current = undefined
      return
    }
    const changedToCard =
      via === SETTLE_VIA.card && prevVia.current !== SETTLE_VIA.card
    prevVia.current = via
    if (changedToCard) refetchCards()
  }, [open, via, refetchCards])
}

export function useSettlePayment(
  args: UseSettlePaymentArgs,
): UseSettlePaymentResult {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    cards: savedCards,
    isLoading: isCardsLoading,
    refetch: refetchCards,
  } = useSettleCards(args.customerId, args.shipmentId, args.open)

  const chargeMutation = useChargeApiV1PaymentsPaymentIdChargePost()
  const markPaidMutation = useMarkPaidApiV1PaymentsPaymentIdMarkPaidPost()
  const uploadMutation = useUploadFilesApiV1FilesPost()
  const [isUploading, setIsUploading] = useState(false)
  const isSubmitting =
    chargeMutation.isPending || markPaidMutation.isPending || isUploading

  const f = useFormState(args.open, args.defaultAmount)
  const { availableMethods, recordedMethods, isMethodsLoading } =
    useSettleMethods(args.open, args.methods)
  const onSelectMethod = (method: string) =>
    f.setSelectedMethod(f.selectedMethod === method ? undefined : method)

  useDefaultCard(savedCards, f.selectedCardId, f.setSelectedCardId)
  useDefaultVia(args.open, isCardsLoading, savedCards.length > 0, f.setVia)
  useRefetchCardsOnCardTab(args.open, f.via, refetchCards)

  const invalidate = () => invalidatePayment(queryClient, args.paymentId)

  const deps: SubmitDeps = {
    args,
    f,
    chargeMutation,
    markPaidMutation,
    uploadMutation,
    setUploading: setIsUploading,
    invalidate,
    t,
  }

  const enteredAmount = parseMoney(f.amount)
  const totalAmount = parseMoney(args.defaultAmount)
  const isAmountOverLimit = enteredAmount > totalAmount
  const isPartialCharge = enteredAmount > 0 && enteredAmount < totalAmount
  const chargeAmountLabel = formatUsd(
    enteredAmount > 0 ? enteredAmount : totalAmount,
    { showCents: true },
  )
  const submit = buildSubmit(deps, isAmountOverLimit)

  return {
    view: f.view,
    via: f.via,
    setVia: f.setVia,
    selectedCardId: f.selectedCardId,
    setSelectedCardId: f.setSelectedCardId,
    selectedMethod: f.selectedMethod,
    onSelectMethod,
    recordedMethods,
    amount: f.amount,
    setAmount: f.setAmount,
    reference: f.reference,
    setReference: f.setReference,
    date: f.date,
    setDate: f.setDate,
    proofFiles: f.proofFiles,
    addProofFiles: (files) => f.setProofFiles([...f.proofFiles, ...files]),
    removeProofFile: (index) =>
      f.setProofFiles(f.proofFiles.filter((_, i) => i !== index)),
    savedCards,
    isCardsLoading,
    availableMethods,
    isMethodsLoading,
    isAmountOverLimit,
    isPartialCharge,
    chargeAmountLabel,
    isSubmitting,
    submit,
  }
}
